// src/pages/GasInvoicePayment.tsx
import React from "react";
import { Box, CircularProgress, Skeleton } from "@mui/material";
import { paymentEmbedEnv } from "../config/env";

const GAS_PAYMENT_URL_WITH_CUSTOMER =
  "https://sfselfservice.supergas-power.co.il/s/invoice-payment";

const GAS_PAYMENT_URL_NO_CUSTOMER =
  "https://sfselfservice.supergas-power.co.il/s/gas-invoice-payment";

const SUPERGAS_PAY_URL = "https://paybill.supergas-power.co.il/";
const SUPERGAS_PAY_ORIGIN = new URL(SUPERGAS_PAY_URL).origin;

const PAYBILL_CUSTOMER_SELECTOR =
  '#CustomerNumber, input[name="CustomerNumber"]';
const PAYBILL_INVOICE_SELECTOR = '#InvoiceNumber, input[name="InvoiceNumber"]';
const PAYBILL_SUBMIT_SELECTOR = '#Submit1, input[name="Submit1"]';

const AUTOFILL_RETRY_DELAYS_MS = [300, 800, 1500, 2500, 4000];
const AUTOSUBMIT_DELAY_MS = 2500;

// For test you can keep this true.
// Later replace with only: paymentEmbedEnv.supergasPayAutofillEnabled
const FORCE_AUTOFILL_FOR_TEST = true;

// For test you can keep this true.
// Later replace with only: paymentEmbedEnv.supergasPayAutosubmitEnabled
const FORCE_AUTOSUBMIT_FOR_TEST = true;

type SupergasPayAutofillMessage = {
  type: "SUPERGAS_PAY_AUTOFILL";
  payload: {
    customerCode: string;
    invoiceNumber: string;
    submit: boolean;
    submitDelayMs: number;
  };
};

function setNativeInputValue(input: HTMLInputElement, value: string) {
  const valueSetter = Object.getOwnPropertyDescriptor(
    HTMLInputElement.prototype,
    "value",
  )?.set;

  if (valueSetter) {
    valueSetter.call(input, value);
  } else {
    input.value = value;
  }

  input.dispatchEvent(new Event("input", { bubbles: true }));
  input.dispatchEvent(new Event("change", { bubbles: true }));
  input.dispatchEvent(new KeyboardEvent("keyup", { bubbles: true }));
}

export function GasInvoicePaymentFrame({
  logisticNumber,
  identificationType,
  identificationValue,
  firmCode,
  iframeUrl,
  title,
}: {
  logisticNumber?: string;
  identificationType?: "invoice" | "id" | "phone";
  identificationValue?: string;
  firmCode?: 3;
  iframeUrl?: string;
  title?: string;
}) {
  const [loading, setLoading] = React.useState(true);
  const iframeRef = React.useRef<HTMLIFrameElement | null>(null);
  const automationTimersRef = React.useRef<number[]>([]);
  const directSubmitScheduledRef = React.useRef(false);

  const useSupergasPayFrame =
    identificationType === "invoice" && firmCode === 3;

  const autofillEnabled =
    FORCE_AUTOFILL_FOR_TEST || paymentEmbedEnv.supergasPayAutofillEnabled;

  const autosubmitEnabled =
    FORCE_AUTOSUBMIT_FOR_TEST || paymentEmbedEnv.supergasPayAutosubmitEnabled;

  const shouldRequestSupergasPayAutomation =
    useSupergasPayFrame &&
    autofillEnabled &&
    Boolean(logisticNumber?.trim()) &&
    Boolean(identificationValue?.trim());

  const iframeSrc = React.useMemo(() => {
    if (iframeUrl) {
      return iframeUrl;
    }

    if (useSupergasPayFrame) {
      return SUPERGAS_PAY_URL;
    }

    if (!logisticNumber) {
      return GAS_PAYMENT_URL_NO_CUSTOMER;
    }

    const url = new URL(GAS_PAYMENT_URL_WITH_CUSTOMER);
    url.searchParams.set("logisticNumber", logisticNumber);

    if (identificationType && identificationValue) {
      url.searchParams.set("identificationValue", identificationValue);
      url.searchParams.set("identificationType", identificationType);
    }

    return url.toString();
  }, [
    identificationType,
    identificationValue,
    iframeUrl,
    logisticNumber,
    useSupergasPayFrame,
  ]);

  const clearAutomationTimers = React.useCallback(() => {
    automationTimersRef.current.forEach((timerId) => {
      window.clearTimeout(timerId);
    });

    automationTimersRef.current = [];
    directSubmitScheduledRef.current = false;
  }, []);

  const tryDirectSupergasPayAutofill = React.useCallback(() => {
    if (!shouldRequestSupergasPayAutomation) {
      return false;
    }

    const iframe = iframeRef.current;
    const customerCode = logisticNumber?.trim();
    const invoiceNumber = identificationValue?.trim();

    if (!iframe || !customerCode || !invoiceNumber) {
      return false;
    }

    try {
      const doc = iframe.contentDocument ?? iframe.contentWindow?.document;

      if (!doc) {
        return false;
      }

      const customerInput = doc.querySelector<HTMLInputElement>(
        PAYBILL_CUSTOMER_SELECTOR,
      );

      const invoiceInput = doc.querySelector<HTMLInputElement>(
        PAYBILL_INVOICE_SELECTOR,
      );

      const submitButton = doc.querySelector<
        HTMLInputElement | HTMLButtonElement
      >(PAYBILL_SUBMIT_SELECTOR);

      if (!customerInput || !invoiceInput) {
        return false;
      }

      setNativeInputValue(customerInput, customerCode);
      setNativeInputValue(invoiceInput, invoiceNumber);

      if (
        autosubmitEnabled &&
        submitButton &&
        !directSubmitScheduledRef.current
      ) {
        directSubmitScheduledRef.current = true;

        const submitTimer = window.setTimeout(() => {
          submitButton.click();
        }, AUTOSUBMIT_DELAY_MS);

        automationTimersRef.current.push(submitTimer);
      }

      return true;
    } catch {
      // Expected when iframe is cross-origin.
      return false;
    }
  }, [
    autosubmitEnabled,
    identificationValue,
    logisticNumber,
    shouldRequestSupergasPayAutomation,
  ]);

  const postSupergasPayAutofillMessage = React.useCallback(() => {
    if (!shouldRequestSupergasPayAutomation) {
      return;
    }

    const frameWindow = iframeRef.current?.contentWindow;
    const customerCode = logisticNumber?.trim();
    const invoiceNumber = identificationValue?.trim();

    if (!frameWindow || !customerCode || !invoiceNumber) {
      return;
    }

    const message: SupergasPayAutofillMessage = {
      type: "SUPERGAS_PAY_AUTOFILL",
      payload: {
        customerCode,
        invoiceNumber,
        submit: autosubmitEnabled,
        submitDelayMs: AUTOSUBMIT_DELAY_MS,
      },
    };

    frameWindow.postMessage(message, SUPERGAS_PAY_ORIGIN);
  }, [
    autosubmitEnabled,
    identificationValue,
    logisticNumber,
    shouldRequestSupergasPayAutomation,
  ]);

  const runSupergasPayAutomation = React.useCallback(() => {
    const directAutofillWorked = tryDirectSupergasPayAutofill();

    if (!directAutofillWorked) {
      postSupergasPayAutofillMessage();
    }
  }, [postSupergasPayAutofillMessage, tryDirectSupergasPayAutofill]);

  React.useEffect(() => {
    setLoading(true);
    clearAutomationTimers();

    return () => {
      clearAutomationTimers();
    };
  }, [clearAutomationTimers, iframeSrc]);

  const handleIframeLoad = React.useCallback(() => {
    setLoading(false);
    clearAutomationTimers();

    if (!shouldRequestSupergasPayAutomation) {
      return;
    }

    runSupergasPayAutomation();

    automationTimersRef.current.push(
      ...AUTOFILL_RETRY_DELAYS_MS.map((delayMs) =>
        window.setTimeout(runSupergasPayAutomation, delayMs),
      ),
    );
  }, [
    clearAutomationTimers,
    runSupergasPayAutomation,
    shouldRequestSupergasPayAutomation,
  ]);

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "70vh",
        borderRadius: 3,
        overflow: "hidden",
        position: "relative",
      }}
    >
      {loading && (
        <>
          <Skeleton
            variant="rectangular"
            width="100%"
            height="110vh"
            animation="wave"
            sx={{
              position: "absolute",
              inset: 0,
              borderRadius: 3,
            }}
          />

          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2,
            }}
          >
            <CircularProgress />
          </Box>
        </>
      )}

      <iframe
        ref={iframeRef}
        scrolling="yes"
        src={iframeSrc}
        title={
          title ??
          (useSupergasPayFrame ? "Supergas pay" : "Gas invoice payment")
        }
        allowFullScreen
        onLoad={handleIframeLoad}
        style={{
          width: "100%",
          height: "110vh",
          border: "none",
          opacity: loading ? 0 : 1,
          transition: "opacity 0.25s ease",
        }}
      />
    </Box>
  );
}
